import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import { AccountsCheckerSummary, checkAccounts } from '../../api/accountsCheckerApi';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload one workbook holding the previous-year and current-year ledger sheets.',
  'Click Check Accounts. The Date column decides which sheet is which year.',
  'Accounts_Checker.xlsx downloads automatically: the current-year sheet with Status and Remark columns.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

function AccountsCheckerPanel({ mission, onBack }: PanelProps) {
  const [files, setFiles] = useState<File[]>([]);
  const { status, error, result, fail, run } = useFileRun<FileResult<AccountsCheckerSummary>>();

  const handleSubmit = async () => {
    if (files.length === 0) {
      fail('Upload the ledger workbook.');
      return;
    }
    await run(() => checkAccounts(files[0]), 'Checking the accounts failed.');
  };

  const s = result?.summary;

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS}>
      <FileDrop
        label="Ledger workbook"
        required
        accept={mission.accept}
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={files}
        onFiles={setFiles}
      />

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Checking…' : 'Check Accounts'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Checking…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && s && (
        <div className="mt-4 space-y-3 text-sm">
          <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">✅ {result.filename} downloaded.</div>
          <div className="rounded-lg border border-border bg-muted px-4 py-3 text-foreground">
            <div>
              Previous year: <strong>{s.previous_sheet}</strong> ({s.previous_from} to {s.previous_to})
            </div>
            <div>
              Current year: <strong>{s.current_sheet}</strong> ({s.current_from} to {s.current_to})
            </div>
            <ul className="mt-2 list-disc pl-5">
              <li>{s.rows} rows checked</li>
              <li>{s.matched} MATCHED</li>
              <li>{s.new} NEW (description not in the previous year)</li>
              <li>{s.not_matched} NOT MATCHED (description under a different account)</li>
            </ul>
          </div>
          {s.warnings.length > 0 && (
            <div className="rounded-lg bg-amber-50 px-4 py-3 text-amber-800">
              <ul className="list-disc pl-5 text-xs">
                {s.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {status === 'idle' && <InfoNotice>Upload the workbook above to run the check.</InfoNotice>}
    </MissionShell>
  );
}

export default AccountsCheckerPanel;
